import type { Tone } from '../../ui/v2';
import { api, apiEndpoints } from '../../utils/apiService';

export const REFUNDS_PAGE_SIZE = 10;

export type RefundStatus =
  | 'Refund Pending'
  | 'Partially Refunded'
  | 'Fully Refunded'
  | 'Not Refunded';

export interface RefundRow {
  id: number;
  order_id: number;
  customer_id: number;
  customer_name: string;
  total_price: string;
  created_at: string;
  refund_status: RefundStatus;
  amount: string;
  reason: string;
}

export type RefundSortKey =
  | 'order_id'
  | 'customer_id'
  | 'created_at'
  | 'total_price'
  | 'amount';

type OrderApi = {
  id: number;
  customer_name?: string | null;
  customer: number;
  created_at: string;
  total_price: number | null;
  refunded_total: number | null;
  is_fully_refunded: boolean;
  status: string;
  is_paid: boolean;
};

/** Refund views; `All` shows every refund row. */
export const REFUND_STATUS_VIEWS = [
  'All',
  'Refund Pending',
  'Partially Refunded',
  'Fully Refunded',
] as const;
export type RefundStatusView = (typeof REFUND_STATUS_VIEWS)[number];

export const REFUND_STATUS_LABEL: Record<RefundStatusView, string> = {
  All: 'All',
  'Refund Pending': 'Refund pending',
  'Partially Refunded': 'Partially refunded',
  'Fully Refunded': 'Fully refunded',
};

/** Always dollars: the refund list ignores the order currency. */
const toUSD = (cents: number | null | undefined) =>
  `$${((cents ?? 0) / 100).toFixed(2)}`;

const isRefundInitiatorStatus = (s?: string) => {
  const v = (s || '').toLowerCase();
  return v === 'canceled' || v === 'cancelled' || v === 'failed';
};

export const deriveRefundStatus = (o: OrderApi): RefundStatus => {
  const refunded = o.refunded_total ?? 0;
  if (o.is_fully_refunded) return 'Fully Refunded';
  if (refunded > 0) return 'Partially Refunded';
  if (o.is_paid && isRefundInitiatorStatus(o.status)) return 'Refund Pending';
  return 'Not Refunded';
};

const orderToRefundRow = (o: OrderApi): RefundRow => ({
  id: o.id,
  order_id: o.id,
  customer_id: o.customer,
  customer_name: o.customer_name ?? '-',
  total_price: toUSD(o.total_price),
  created_at: o.created_at,
  refund_status: deriveRefundStatus(o),
  amount: toUSD(o.refunded_total ?? 0),
  reason: '-',
});

const LISTED = new Set<RefundStatus>([
  'Refund Pending',
  'Partially Refunded',
  'Fully Refunded',
]);

export async function fetchRefundOrders(): Promise<RefundRow[]> {
  const res = await api.get(apiEndpoints.getLegacyOrders());
  if (!res || !res.ok) {
    throw new Error(`HTTP ${res?.status || 'Unknown'}`);
  }

  const json = await res.json();
  const orders: OrderApi[] = Array.isArray(json) ? json : (json.results ?? []);

  return orders
    .filter((o) => LISTED.has(deriveRefundStatus(o)))
    .sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    )
    .map(orderToRefundRow);
}

export const refundStatusTone = (status: RefundStatus): Tone => {
  if (status === 'Fully Refunded') return 'success';
  if (status === 'Partially Refunded') return 'accent';
  if (status === 'Refund Pending') return 'warning';
  return 'neutral';
};

const dollars = (value: string) =>
  parseFloat((value || '').replace(/[^\d.]/g, ''));

export function filterRefunds(
  rows: RefundRow[],
  view: RefundStatusView,
): RefundRow[] {
  return view === 'All' ? rows : rows.filter((r) => r.refund_status === view);
}

export function sortRefunds(
  rows: RefundRow[],
  key: RefundSortKey,
  dir: 'asc' | 'desc',
): RefundRow[] {
  return [...rows].sort((a, b) => {
    let valA: any = a[key];
    let valB: any = b[key];
    if (key === 'created_at') {
      valA = new Date(valA).getTime();
      valB = new Date(valB).getTime();
    }
    if (key === 'order_id' || key === 'customer_id') {
      valA = Number(valA);
      valB = Number(valB);
    }
    if (key === 'total_price' || key === 'amount') {
      valA = dollars(valA);
      valB = dollars(valB);
    }
    if (valA < valB) return dir === 'asc' ? -1 : 1;
    if (valA > valB) return dir === 'asc' ? 1 : -1;
    return 0;
  });
}

/** Figures over the filtered rows (not just the visible page). */
export function refundTotals(rows: RefundRow[]) {
  return {
    refundCount: rows.length,
    refundedAmountTotal: rows.reduce((sum, r) => {
      const amount = dollars(r.amount);
      return sum + (Number.isNaN(amount) ? 0 : amount);
    }, 0),
  };
}
